import { Outlet } from 'react-router-dom';
import NavBar from './NavBar';
import Sidebar from './Sidebar';
import { UserAvatarImg } from '@/components/Common/UserAvatarImg';
import WarbachFeedbackWidget from '@/components/Common/WarbachFeedbackWidget';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setViewAsRole, setViewAsUser } from '@/store/slices/authSlice';

export default function Layout() {
  const dispatch = useAppDispatch();
  const { viewAsRole, viewAsUser } = useAppSelector((s) => s.auth);
  const inPreview = !!(viewAsRole || viewAsUser);

  function exitPreview() {
    dispatch(setViewAsRole(null));
    dispatch(setViewAsUser(null));
  }

  return (
    <div className="min-h-screen bg-darkbg flex flex-col">
      {/* Ambient glow behind the navbar */}
      <div className="fixed inset-x-0 top-0 h-72 bg-gold-radial pointer-events-none z-0" />

      <NavBar />

      {/* ── Admin preview banner ── */}
      {inPreview && (
        <div className="relative z-20 bg-amber-500/90 text-darkbg text-sm font-semibold py-2 px-4 flex items-center justify-center gap-3 shadow">
          {viewAsUser ? (
            <span className="flex items-center gap-2">
              👁️ Admin Preview: viewing as
              <UserAvatarImg
                avatarUrl={viewAsUser.avatarUrl}
                name={viewAsUser.displayName ?? viewAsUser.username}
                size="xs"
                className="border border-darkbg/30 inline-block"
                fallbackClassName="border-darkbg/30 bg-darkbg/10 inline-flex"
              />
              <strong>{viewAsUser.displayName ?? viewAsUser.username}</strong>
              <span className="font-normal opacity-70">({viewAsUser.role})</span>
              — navigation, characters &amp; coin balance reflect their account.
            </span>
          ) : (
            <span>
              👁️ Admin Preview: viewing as role <span className="uppercase">{viewAsRole}</span>
              &nbsp;— navigation only; your data is unchanged.
            </span>
          )}
          <button
            onClick={exitPreview}
            className="underline hover:no-underline text-darkbg/80 hover:text-darkbg transition-colors ml-2"
          >
            Exit Preview
          </button>
        </div>
      )}

      {/* pt-6 on all sizes; pb-24 on mobile so the fixed Warbach widget never
          covers the last row of content.  lg:pb-6 restores normal spacing when
          the sidebar is visible and content is wider / less likely to scroll to
          an edge. */}
      <div className="relative z-10 flex flex-1 max-w-7xl mx-auto w-full gap-6 px-4 pt-6 pb-24 lg:pb-6">
        <Sidebar />
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>

      {/* Warbach feedback widget — fixed floating button, visible on every page */}
      <WarbachFeedbackWidget />
    </div>
  );
}
